import { Card, CardContent, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { OnlyFansDailyStats } from '../../../../../../hooks/useMetricsDailyStats';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

const formatNumber = (num: number): string => {
    return num.toLocaleString();
};

// Get visitor data for charts
const getVisitorChartData = (
    data: OnlyFansDailyStats[],
): {
    date: string;
    dateFormatted: string;
    visitors: number;
    delta: number;
}[] => {
    if (!data || data.length === 0) return [];

    return data
        .map(day => {
            return {
                date: new Date(day._id).toISOString(),
                dateFormatted: moment(day._id).format('MMM D, YYYY'),
                visitors: day.profileVisitors?.visitors?.visitors?.total || 0,
                delta: day.profileVisitors?.visitors?.visitors?.delta || 0,
            };
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(-30); // Last 30 days
};

const VisitorTrendsChart: React.FC<DashboardProps> = ({ subscriber, data, isLoading }) => {
    const theme = useTheme();

    if (isLoading) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="rectangular" width="100%" height={300} />
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Stack direction="column" spacing={1} mb={2}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" gutterBottom>
                                Visitor Trends (Last 30 Days)
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        No visitor data available.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const visitorData = getVisitorChartData(data);

    if (visitorData.length === 0) return null;

    const fromDate = moment().subtract(30, 'days');
    const toDate = moment();

    return (
        <Card elevation={3}>
            <CardContent>
                <Stack direction="column" spacing={1} mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="h6" gutterBottom>
                            Visitor Trends (Last 30 Days)
                        </Typography>
                        <ExportButtons
                            data={visitorData}
                            componentType="visitorTrends"
                            filename={`${subscriber.username}_Visitor_Trends_${fromDate.format('YYYY-MM-DD')}_${toDate.format(
                                'YYYY-MM-DD',
                            )}`}
                        />
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        {fromDate.format('MMM D, YYYY')} - {toDate.format('MMM D, YYYY')}
                    </Typography>
                </Stack>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={visitorData} margin={{ left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dateFormatted" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={value => formatNumber(value as number)} />
                        <YAxis
                            dataKey="delta"
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 11 }}
                            tickFormatter={value => formatNumber(value as number)}
                        />
                        <Tooltip
                            labelFormatter={label => `Date: ${label}`}
                            formatter={(value, name) => [formatNumber(value as number), name]}
                        />
                        <Line
                            type="monotone"
                            dataKey="visitors"
                            name="Profile Visitors"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={{ r: 0 }}
                            activeDot={{ r: 3 }}
                        />
                        <Bar
                            dataKey="delta"
                            name="Difference"
                            fill={theme.palette.secondary.main}
                            fillOpacity={0.4}
                            barSize={10}
                            yAxisId="right"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default VisitorTrendsChart;
